import SopRoleContent from '../components/SopRoleContent';
import React, {
  useEffect,
  useState
} from 'react';
import {
  get,
  post,
  put,
  del,
  getProtectedBlob
} from '../utils/api';
import {
  useAuth
} from '../context/AuthContext';

const CATEGORIES = [
  'HR Policies',
  'Factory SOPs',
  'Sales SOPs',
  'Admin SOPs',
  'General Policies',
  'Finance',
  'Health & Safety'
];

const CAT_COLORS = {
  'HR Policies': {
    bg:'#EDF4FF',
    color:'#1E4FAD'
  },
  'Factory SOPs': {
    bg:'#FFF3DC',
    color:'#8B5E0A'
  },
  'Sales SOPs': {
    bg:'#E8F5EB',
    color:'#2E6B3A'
  },
  'Admin SOPs': {
    bg:'#F2EFE7',
    color:'#556B2F'
  },
  'General Policies': {
    bg:'#FDEAEA',
    color:'#8F2E24'
  },
  'Finance': {
    bg:'#F0E6FF',
    color:'#6B2FAD'
  },
  'Health & Safety': {
    bg:'#E6FFF0',
    color:'#0A8B5E'
  }
};

const emptyForm = {
  document_code:'',
  title:'',
  category:'Admin SOPs',
  description:'',
  doc_type:'inline',
  content:'',
  google_doc_url:'',
  version:'1.0',
  applies_to_roles:[],
  applies_to_all:false,
  effective_date:'',
  review_date:'',
  change_summary:''
};

const typeLabel = type => {
  if (type === 'inline') return 'BLOANS SOP';
  if (type === 'pdf') return 'PDF';
  return 'GOOGLE DOC';
};

const roleLabel = role =>
  String(role || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, value =>
      value.toUpperCase()
    );

const dateOnly = value =>
  value
    ? new Date(value)
        .toLocaleDateString(
          'en-NG',
          {
            day:'numeric',
            month:'short',
            year:'numeric'
          }
        )
    : 'Not set';

function ProtectedPdfViewer({
  filename,
  title
}) {
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let objectUrl = '';

    setPdfUrl('');
    setPdfError('');
    setPdfLoading(true);

    if (!filename) {
      setPdfError(
        'No PDF document is attached to this SOP.'
      );
      setPdfLoading(false);

      return () => {
        active = false;
      };
    }

    getProtectedBlob(
      '/sops/pdf/' +
      encodeURIComponent(filename)
    )
      .then(blob => {
        if (!active) {
          return;
        }

        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      })
      .catch(error => {
        if (!active) {
          return;
        }

        setPdfError(
          error?.message ||
          'This SOP PDF could not be opened.'
        );
      })
      .finally(() => {
        if (active) {
          setPdfLoading(false);
        }
      });

    return () => {
      active = false;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [filename]);

  if (pdfLoading) {
    return (
      <div
        style={{
          padding:40,
          textAlign:'center',
          color:'#556B2F',
          background:'#F2EFE7',
          borderRadius:12
        }}
      >
        Loading secure SOP document...
      </div>
    );
  }

  if (pdfError) {
    return (
      <div
        style={{
          padding:24,
          color:'#8F2E24',
          background:'#FDEAEA',
          borderRadius:12,
          lineHeight:1.6
        }}
      >
        <strong>
          This SOP could not be opened.
        </strong>

        <div style={{marginTop:8}}>
          {pdfError}
        </div>
      </div>
    );
  }

  return (
    <iframe
      src={pdfUrl}
      title={title}
      style={{
        width:'100%',
        height:650,
        border:'none'
      }}
    />
  );
}

export default function SOPs() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [selectedDoc, setSelectedDoc] =
    useState(null);
  const [loading, setLoading] =
    useState(true);
  // SOP REQUEST ERROR STATE
  const [loadError, setLoadError] =
    useState('');
  const [searchTerm, setSearchTerm] =
    useState('');
  const [filterCat, setFilterCat] =
    useState('');
  const [showCreate, setShowCreate] =
    useState(false);
  // DEDICATED SOP PDF UPLOAD MODAL
  const [showUpload, setShowUpload] =
    useState(false);

  const isSuperAdmin =
    user?.role === 'super_admin';

  const load = async () => {
    setLoading(true);
    setLoadError('');

    try {
      const result =
        await get('/sops');

      setDocs(result.data || []);
    } catch (error) {
      setDocs([]);
      setLoadError(
        error.message ||
        'The SOP Library could not be loaded.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (selectedDoc) {
    return (
      <DocumentView
        docId={selectedDoc}
        isSuperAdmin={isSuperAdmin}
        onBack={() => {
          setSelectedDoc(null);
          load();
        }}
        onOpenRelated={setSelectedDoc}
      />
    );
  }

  const filtered = docs.filter(doc => {
    const categoryMatch =
      !filterCat ||
      doc.category === filterCat;

    const searchMatch =
      !searchTerm ||
      [
        doc.title,
        doc.document_code,
        doc.description
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(
          searchTerm.toLowerCase()
        );

    return (
      categoryMatch &&
      searchMatch
    );
  });

  const categories = [
    ...new Set(
      filtered.map(
        doc => doc.category
      )
    )
  ];

  const unreadCount =
    docs.filter(
      doc =>
        doc.status === 'published' &&
        doc.latest_update_id &&
        !Number(
          doc.current_user_read
        )
    ).length;

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">
            SOPs & Policies
          </h2>
          <p
            className="text-muted text-sm"
            style={{marginTop:4}}
          >
            Role-assigned operating procedures,
            policies and required reading
          </p>
        </div>

        {isSuperAdmin && (
          <div
            style={{
              display:'flex',
              gap:8,
              flexWrap:'wrap',
              justifyContent:'flex-end'
            }}
          >
            <button
              className="btn btn-outline"
              onClick={() =>
                setShowUpload(true)
              }
            >
              Upload SOP Document
            </button>

            <button
              className="btn btn-primary"
              onClick={() =>
                setShowCreate(true)
              }
            >
              + Write Inline SOP
            </button>
          </div>
        )}
      </div>

      {unreadCount > 0 && (
        <div
          style={{
            background:'#FDEAEA',
            border:'1px solid #F5B8B8',
            borderRadius:8,
            padding:'12px 18px',
            marginBottom:20,
            color:'#8F2E24',
            fontSize:13
          }}
        >
          You have <strong>{unreadCount}</strong>{' '}
          SOP update
          {unreadCount !== 1 ? 's' : ''}{' '}
          awaiting your reading confirmation.
        </div>
      )}

      <div
        className="search-bar"
        style={{marginBottom:20}}
      >
        <input
          value={searchTerm}
          placeholder="Search SOPs..."
          onChange={event =>
            setSearchTerm(
              event.target.value
            )
          }
          style={{flex:1}}
        />

        <select
          value={filterCat}
          onChange={event =>
            setFilterCat(
              event.target.value
            )
          }
        >
          <option value="">
            All Categories
          </option>

          {CATEGORIES.map(category => (
            <option
              key={category}
              value={category}
            >
              {category}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div
          className="spinner"
          style={{marginTop:40}}
        />
      ) : loadError ? (
        <div
          className="empty-state"
          style={{
            padding:40,
            color:'#8F2E24'
          }}
        >
          <div style={{
            fontWeight:700,
            marginBottom:8
          }}>
            SOP Library could not be loaded
          </div>
          <div style={{marginBottom:16}}>
            {loadError}
          </div>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={load}
          >
            Try Again
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="empty-state"
          style={{padding:40}}
        >
          No SOPs are assigned to you.
        </div>
      ) : (
        categories.map(category => {
          const categoryDocs =
            filtered.filter(
              doc =>
                doc.category ===
                category
            );

          const colors =
            CAT_COLORS[category] ||
            {
              bg:'#F0F0F0',
              color:'#555'
            };

          return (
            <div
              key={category}
              style={{marginBottom:28}}
            >
              <div
                style={{
                  fontSize:12,
                  fontWeight:700,
                  color:colors.color,
                  marginBottom:10,
                  textTransform:'uppercase',
                  letterSpacing:'.08em'
                }}
              >
                {category}
              </div>

              <div
                style={{
                  display:'grid',
                  gridTemplateColumns:
                    'repeat(auto-fill,minmax(285px,1fr))',
                  gap:12
                }}
              >
                {categoryDocs.map(doc => {
                  const unread =
                    doc.status ===
                      'published' &&
                    doc.latest_update_id &&
                    !Number(
                      doc.current_user_read
                    );

                  return (
                    <button
                      type="button"
                      key={doc.id}
                      onClick={() =>
                        setSelectedDoc(doc.id)
                      }
                      style={{
                        background:'#fff',
                        border:
                          '2px solid ' +
                          (
                            unread
                              ? '#8F2E24'
                              : 'var(--border-light)'
                          ),
                        borderRadius:10,
                        padding:16,
                        textAlign:'left',
                        cursor:'pointer',
                        boxShadow:'var(--shadow)'
                      }}
                    >
                      <div
                        style={{
                          display:'flex',
                          justifyContent:
                            'space-between',
                          gap:8,
                          marginBottom:8
                        }}
                      >
                        <div
                          style={{
                            fontSize:14,
                            fontWeight:700,
                            color:'#3B2A21'
                          }}
                        >
                          {doc.title}
                        </div>

                        <span
                          style={{
                            fontSize:9,
                            background:colors.bg,
                            color:colors.color,
                            padding:'3px 8px',
                            borderRadius:20,
                            whiteSpace:'nowrap'
                          }}
                        >
                          {typeLabel(
                            doc.doc_type
                          )}
                        </span>
                      </div>

                      {doc.document_code && (
                        <div
                          style={{
                            fontSize:10,
                            color:'#A8893D',
                            fontWeight:700,
                            marginBottom:6
                          }}
                        >
                          {doc.document_code}
                        </div>
                      )}

                      {doc.description && (
                        <div
                          style={{
                            fontSize:12,
                            color:'#777',
                            lineHeight:1.5,
                            marginBottom:10
                          }}
                        >
                          {doc.description}
                        </div>
                      )}

                      <div
                        style={{
                          display:'flex',
                          justifyContent:
                            'space-between',
                          alignItems:'center',
                          gap:8
                        }}
                      >
                        <span
                          style={{
                            fontSize:10,
                            color:'#777'
                          }}
                        >
                          Version {doc.version}
                        </span>

                        {isSuperAdmin ? (
                          <span
                            style={{
                              fontSize:9,
                              fontWeight:700,
                              color:
                                doc.status ===
                                  'published'
                                  ? '#2E6B3A'
                                  : doc.status ===
                                      'retired'
                                    ? '#8F2E24'
                                    : '#8B5E0A'
                            }}
                          >
                            {String(
                              doc.status ||
                              'draft'
                            ).toUpperCase()}
                          </span>
                        ) : unread ? (
                          <span
                            style={{
                              fontSize:9,
                              color:'#8F2E24',
                              fontWeight:700
                            }}
                          >
                            CONFIRM READING
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize:9,
                              color:'#2E6B3A',
                              fontWeight:700
                            }}
                          >
                            UP TO DATE
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      {showCreate && (
        <DocumentModal
          initialDocType="inline"
          onClose={() =>
            setShowCreate(false)
          }
          onSaved={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}

      {showUpload && (
        <DocumentModal
          initialDocType="pdf"
          uploadOnly
          onClose={() =>
            setShowUpload(false)
          }
          onSaved={() => {
            setShowUpload(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function DocumentView({
  docId,
  isSuperAdmin,
  onBack,
  onOpenRelated
}) {
  const {user} = useAuth();
  const [doc, setDoc] =
    useState(null);
  const [loading, setLoading] =
    useState(true);
  const [documentError, setDocumentError] =
    useState('');
  const [showEdit, setShowEdit] =
    useState(false);
  const [confirmations, setConfirmations] =
    useState(null);

  const load = async () => {
    setLoading(true);
    setDocumentError('');

    try {
      setDoc(
        await get(
          '/sops/' + docId
        )
      );
    } catch (error) {
      setDoc(null);
      setDocumentError(
        error.message ||
        'This SOP could not be opened.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [docId]);

  const confirmRead = async updateId => {
    try {
      await post(
        '/sops/updates/' +
          updateId +
          '/confirm',
        {}
      );

      await load();
    } catch (error) {
      alert(error.message);
    }
  };

  const loadConfirmations =
    async updateId => {
      try {
        setConfirmations(
          await get(
            '/sops/updates/' +
              updateId +
              '/confirmations'
          )
        );
      } catch (error) {
        alert(error.message);
      }
    };

  const notifyAssigned = async () => {
    if (
      !window.confirm(
        'Send this SOP reminder to its assigned staff?'
      )
    ) {
      return;
    }

    try {
      const result = await post(
        '/sops/' +
          doc.id +
          '/notify',
        {}
      );

      alert(
        result.notified_users +
        ' assigned user(s) notified.'
      );
    } catch (error) {
      alert(error.message);
    }
  };

  const retire = async () => {
    if (
      !window.confirm(
        'Retire this SOP? Staff will no longer see it, but its history will remain.'
      )
    ) {
      return;
    }

    try {
      await del(
        '/sops/' + doc.id
      );

      onBack();
    } catch (error) {
      alert(error.message);
    }
  };

  if (loading) {
    return (
      <div
        className="spinner"
        style={{marginTop:40}}
      />
    );
  }

  if (documentError || !doc) {
    return (
      <div
        className="empty-state"
        style={{
          padding:40,
          color:'#8F2E24'
        }}
      >
        <div style={{
          fontWeight:700,
          marginBottom:8
        }}>
          This SOP could not be opened
        </div>
        <div style={{marginBottom:16}}>
          {documentError ||
            'The SOP record was not returned.'}
        </div>
        <button
          type="button"
          className="btn btn-outline btn-sm"
          onClick={onBack}
        >
          Back to SOP Library
        </button>
      </div>
    );
  }

  const latest =
    doc.updates?.[0];

  const colors =
    CAT_COLORS[doc.category] ||
    {
      bg:'#F0F0F0',
      color:'#555'
    };

  return (
    <div>
      <button
        className="btn btn-ghost btn-sm"
        onClick={onBack}
        style={{marginBottom:16}}
      >
        ← Back to SOPs
      </button>

      <div
        style={{
          background:'#3B2A21',
          borderRadius:10,
          padding:'20px 24px',
          marginBottom:16
        }}
      >
        <div
          style={{
            display:'flex',
            justifyContent:
              'space-between',
            gap:16
          }}
        >
          <div>
            <span
              style={{
                display:'inline-block',
                background:colors.bg,
                color:colors.color,
                padding:'3px 10px',
                borderRadius:20,
                fontSize:10,
                fontWeight:700
              }}
            >
              {doc.category}
            </span>

            <h2
              style={{
                color:'#A8893D',
                margin:'10px 0 5px'
              }}
            >
              {doc.title}
            </h2>

            <div
              style={{
                color:'rgba(242,239,231,.65)',
                fontSize:12
              }}
            >
              {doc.document_code &&
                doc.document_code + ' • '}
              Version {doc.version} •{' '}
              {typeLabel(doc.doc_type)}
            </div>
          </div>

          {isSuperAdmin && (
            <div
              style={{
                display:'flex',
                gap:7,
                flexWrap:'wrap',
                justifyContent:'flex-end'
              }}
            >
              {doc.status ===
                'published' && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={notifyAssigned}
                  style={{
                    color:'#A8893D',
                    borderColor:'#A8893D'
                  }}
                >
                  Notify Assigned Staff
                </button>
              )}

              {doc.status !==
                'retired' && (
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() =>
                    setShowEdit(true)
                  }
                >
                  {doc.status === 'draft'
                    ? 'Edit Draft'
                    : 'Publish Revision'}
                </button>
              )}

              {doc.status !==
                'retired' && (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={retire}
                >
                  Retire
                </button>
              )}
            </div>
          )}
        </div>

        {doc.description && (
          <p
            style={{
              color:'rgba(242,239,231,.65)',
              fontSize:13,
              marginBottom:0
            }}
          >
            {doc.description}
          </p>
        )}
      </div>

      <div
        style={{
          display:'grid',
          gridTemplateColumns:
            'repeat(auto-fit,minmax(160px,1fr))',
          gap:10,
          marginBottom:16
        }}
      >
        <InfoBox
          label="Status"
          value={roleLabel(doc.status)}
        />
        <InfoBox
          label="Effective Date"
          value={dateOnly(
            doc.effective_date
          )}
        />
        <InfoBox
          label="Review Date"
          value={dateOnly(
            doc.review_date
          )}
        />
        <InfoBox
          label="Assigned To"
          value={
            doc.applies_to_all
              ? 'All Staff'
              : (
                  doc.applies_to_roles ||
                  []
                )
                  .map(roleLabel)
                  .join(', ')
          }
        />
      </div>

      {latest &&
       !latest.user_read &&
       doc.status ===
         'published' && (
        <div
          style={{
            background:'#FDEAEA',
            border:'1px solid #F5B8B8',
            borderRadius:8,
            padding:16,
            marginBottom:16
          }}
        >
          <div
            style={{
              fontSize:13,
              fontWeight:700,
              color:'#8F2E24'
            }}
          >
            Reading confirmation required
          </div>

          <div
            style={{
              fontSize:12,
              color:'#8F2E24',
              margin:'5px 0 10px'
            }}
          >
            {latest.update_summary}
          </div>

          <button
            className="btn btn-primary btn-sm"
            onClick={() =>
              confirmRead(latest.id)
            }
          >
            I Have Read and Understood
          </button>
        </div>
      )}

      <div
        style={{
          background:'#fff',
          border:
            '1px solid var(--border-light)',
          borderRadius:10,
          overflow:'hidden',
          marginBottom:16
        }}
      >
        <div
          style={{
            padding:'12px 16px',
            background:'#F2EFE7',
            borderBottom:
              '1px solid var(--border-light)',
            fontWeight:700,
            color:'#556B2F'
          }}
        >
          SOP Document
        </div>

        {doc.doc_type ===
          'inline' ? (
          <SopRoleContent content={doc.content} role={user?.role} />
        ) : doc.doc_type ===
            'google_doc' &&
          doc.google_doc_url ? (
          <iframe
            src={doc.google_doc_url
              .replace(
                '/edit',
                '/preview'
              )
              .replace(
                '/view',
                '/preview'
              )}
            title={doc.title}
            style={{
              width:'100%',
              height:650,
              border:'none'
            }}
          />
        ) : doc.doc_type ===
            'pdf' ? (
          <ProtectedPdfViewer
            filename={doc.pdf_filename}
            title={doc.title}
          />
        ) : (
          <div
            style={{
              padding:30,
              color:'#8F2E24'
            }}
          >
            This document has no readable
            content configured.
          </div>
        )}
      </div>

      {doc.related_documents?.length >
        0 && (
        <Section title="Connected SOPs">
          {doc.related_documents.map(
            related => (
              <button
                key={related.id}
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() =>
                  onOpenRelated(
                    related.id
                  )
                }
                style={{
                  marginRight:8,
                  marginBottom:8
                }}
              >
                {related.document_code
                  ? related.document_code +
                    ' — '
                  : ''}
                {related.title}
              </button>
            )
          )}
        </Section>
      )}

      {doc.versions?.length > 0 && (
        <Section title="Version History">
          {doc.versions.map(version => (
            <div
              key={version.id}
              style={{
                padding:'10px 0',
                borderBottom:
                  '1px solid #F2EFE7'
              }}
            >
              <strong>
                Version {version.version}
              </strong>
              {' — '}
              {version.change_summary}
              <div
                style={{
                  fontSize:11,
                  color:'#888',
                  marginTop:3
                }}
              >
                {dateOnly(
                  version.created_at
                )}
                {' • '}
                {roleLabel(
                  version.publication_status
                )}
              </div>
            </div>
          ))}
        </Section>
      )}

      {doc.updates?.length > 0 && (
        <Section title="Reading Confirmations">
          {doc.updates.map(update => (
            <div
              key={update.id}
              style={{
                padding:'10px 0',
                borderBottom:
                  '1px solid #F2EFE7'
              }}
            >
              <div>
                {update.update_summary}
              </div>
              <div
                style={{
                  fontSize:11,
                  color:'#777',
                  marginTop:4
                }}
              >
                {update.read_count} of{' '}
                {doc.total_staff} assigned
                staff confirmed
              </div>

              {isSuperAdmin && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() =>
                    loadConfirmations(
                      update.id
                    )
                  }
                >
                  View Staff
                </button>
              )}
            </div>
          ))}
        </Section>
      )}

      {confirmations && (
        <div className="modal-overlay">
          <div
            className="modal"
            style={{maxWidth:560}}
          >
            <div className="modal-hdr">
              <span className="modal-title">
                Reading Confirmations
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  setConfirmations(null)
                }
              >
                X
              </button>
            </div>

            <div className="modal-body">
              <StaffList
                title="Confirmed"
                staff={
                  confirmations.confirmed ||
                  []
                }
                color="#2E6B3A"
              />

              <StaffList
                title="Pending"
                staff={
                  confirmations.pending ||
                  []
                }
                color="#8F2E24"
              />
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <DocumentModal
          document={doc}
          onClose={() =>
            setShowEdit(false)
          }
          onSaved={() => {
            setShowEdit(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function InfoBox({label, value}) {
  return (
    <div
      style={{
        background:'#fff',
        border:
          '1px solid var(--border-light)',
        borderRadius:8,
        padding:12
      }}
    >
      <div
        style={{
          fontSize:10,
          color:'#888',
          textTransform:'uppercase',
          marginBottom:5
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize:12,
          color:'#3B2A21',
          fontWeight:600
        }}
      >
        {value || 'Not assigned'}
      </div>
    </div>
  );
}

function Section({title, children}) {
  return (
    <div
      style={{
        background:'#fff',
        border:
          '1px solid var(--border-light)',
        borderRadius:10,
        marginBottom:16,
        overflow:'hidden'
      }}
    >
      <div
        style={{
          padding:'12px 16px',
          background:'#F2EFE7',
          fontWeight:700,
          color:'#556B2F'
        }}
      >
        {title}
      </div>
      <div style={{padding:16}}>
        {children}
      </div>
    </div>
  );
}

function StaffList({
  title,
  staff,
  color
}) {
  return (
    <div style={{marginBottom:18}}>
      <div
        style={{
          fontSize:12,
          fontWeight:700,
          color,
          marginBottom:6
        }}
      >
        {title} ({staff.length})
      </div>

      {staff.length === 0 ? (
        <div
          style={{
            fontSize:12,
            color:'#888'
          }}
        >
          None
        </div>
      ) : (
        staff.map((person, index) => (
          <div
            key={index}
            style={{
              padding:'6px 0',
              borderBottom:
                '1px solid #F2EFE7',
              fontSize:12
            }}
          >
            {person.name}
            {' — '}
            {roleLabel(person.role)}
          </div>
        ))
      )}
    </div>
  );
}

function AudienceFields({
  form,
  setForm,
  roles
}) {
  const toggleRole = role => {
    const current =
      form.applies_to_roles || [];

    setForm({
      ...form,
      applies_to_roles:
        current.includes(role)
          ? current.filter(
              item => item !== role
            )
          : [...current, role]
    });
  };

  return (
    <div className="form-group">
      <label className="form-label">
        Assigned Audience *
      </label>

      <label
        style={{
          display:'flex',
          gap:7,
          alignItems:'center',
          marginBottom:10,
          fontSize:13
        }}
      >
        <input
          type="checkbox"
          checked={
            form.applies_to_all
          }
          onChange={event =>
            setForm({
              ...form,
              applies_to_all:
                event.target.checked,
              applies_to_roles:
                event.target.checked
                  ? []
                  : form
                      .applies_to_roles
            })
          }
        />
        All staff
      </label>

      {!form.applies_to_all && (
        <div
          style={{
            display:'grid',
            gridTemplateColumns:
              'repeat(auto-fit,minmax(190px,1fr))',
            gap:7,
            maxHeight:180,
            overflowY:'auto',
            border:'1px solid var(--border-light)',
            padding:10,
            borderRadius:7
          }}
        >
          {roles.map(role => (
            <label
              key={role.name}
              style={{
                display:'flex',
                gap:7,
                alignItems:'center',
                fontSize:12
              }}
            >
              <input
                type="checkbox"
                checked={
                  form
                    .applies_to_roles
                    ?.includes(
                      role.name
                    ) || false
                }
                onChange={() =>
                  toggleRole(role.name)
                }
              />
              {role.label ||
                roleLabel(role.name)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentModal({
  document,
  initialDocType='inline',
  uploadOnly=false,
  onClose,
  onSaved
}) {
  const editing = Boolean(document);

  const [form, setForm] =
    useState(() => ({
      ...emptyForm,
      doc_type:
        document?.doc_type ||
        initialDocType,
      ...(document || {}),
      content:
        document?.content || '',
      google_doc_url:
        document?.google_doc_url ||
        '',
      document_code:
        document?.document_code ||
        '',
      effective_date:
        document?.effective_date
          ? String(
              document.effective_date
            ).slice(0,10)
          : '',
      review_date:
        document?.review_date
          ? String(
              document.review_date
            ).slice(0,10)
          : '',
      applies_to_roles:
        document
          ?.applies_to_roles ||
        []
    }));

  const [roles, setRoles] =
    useState([]);
  const [file, setFile] =
    useState(null);
  const [saving, setSaving] =
    useState(false);
  const [error, setError] =
    useState('');

  useEffect(() => {
    get('/sops/audience-options')
      .then(result =>
        setRoles(result.data || [])
      )
      .catch(apiError =>
        setError(apiError.message)
      );
  }, []);

  const validate = () => {
    if (
      !String(
        form.document_code || ''
      ).trim() ||
      !form.title ||
      !form.category ||
      !String(
        form.version || ''
      ).trim()
    ) {
      return 'Document code, title, category and version are required.';
    }

    if (
      form.doc_type === 'pdf' &&
      !editing &&
      !file
    ) {
      return 'Select a PDF file.';
    }

    if (
      file &&
      file.type !== 'application/pdf'
    ) {
      return 'Only PDF documents can be uploaded.';
    }

    if (
      file &&
      file.size > 20 * 1024 * 1024
    ) {
      return 'The PDF must not exceed 20 MB.';
    }

    if (
      !form.applies_to_all &&
      (
        !form.applies_to_roles ||
        form.applies_to_roles
          .length === 0
      )
    ) {
      return 'Select at least one assigned role.';
    }

    return '';
  };

  const uploadPdfDraft = async () => {
    if (!file) {
      throw new Error(
        'Select a PDF file.'
      );
    }

    const payload =
      new FormData();

    payload.append(
      'pdf',
      file
    );
    payload.append(
      'document_code',
      String(
        form.document_code || ''
      )
        .trim()
        .toUpperCase()
    );
    payload.append(
      'title',
      form.title
    );
    payload.append(
      'category',
      form.category
    );
    payload.append(
      'description',
      form.description || ''
    );
    payload.append(
      'version',
      form.version || '1.0'
    );
    payload.append(
      'applies_to_roles',
      JSON.stringify(
        form.applies_to_roles || []
      )
    );
    payload.append(
      'applies_to_all',
      String(
        Boolean(form.applies_to_all)
      )
    );
    payload.append(
      'effective_date',
      form.effective_date || ''
    );
    payload.append(
      'review_date',
      form.review_date || ''
    );

    const token =
      localStorage.getItem(
        'bb_token'
      );

    const response =
      await fetch(
        '/api/sops/upload-pdf',
        {
          method:'POST',
          headers:{
            Authorization:
              'Bearer ' + token
          },
          body:payload
        }
      );

    const data =
      await response.json();

    if (!response.ok) {
      throw new Error(
        data.error ||
        'PDF upload failed'
      );
    }

    return data;
  };

  const saveDraft = async () => {
    const validationError =
      validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (
        !editing &&
        form.doc_type === 'pdf'
      ) {
        await uploadPdfDraft();
      } else if (editing) {
        await put(
          '/sops/' +
            document.id,
          form
        );
      } else {
        await post(
          '/sops',
          form
        );
      }

      onSaved();
    } catch (apiError) {
      setError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    const validationError =
      validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    if (
      !String(
        form.change_summary || ''
      ).trim()
    ) {
      setError(
        'Enter a publication or revision summary.'
      );
      return;
    }

    setSaving(true);
    setError('');

    try {
      let documentId =
        document?.id;

      if (!documentId) {
        if (
          form.doc_type === 'pdf'
        ) {
          const created =
            await uploadPdfDraft();

          documentId =
            created.id;
        } else {
          const created =
            await post(
              '/sops',
              form
            );

          documentId =
            created.id;
        }
      }

      await post(
        '/sops/' +
          documentId +
          '/publish',
        form
      );

      onSaved();
    } catch (apiError) {
      setError(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  const isPublished =
    document?.status ===
    'published';

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        style={{
          maxWidth:780,
          maxHeight:'92vh',
          overflowY:'auto'
        }}
      >
        <div className="modal-hdr">
          <span className="modal-title">
            {isPublished
              ? 'Publish SOP Revision'
              : editing
                ? 'Edit SOP Draft'
                : uploadOnly
                  ? 'Upload SOP Document'
                  : 'Write Inline SOP'}
          </span>

          <button
            className="btn btn-ghost btn-sm"
            onClick={onClose}
          >
            X
          </button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-error mb-md">
              {error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Document Code
              </label>
              <input
                className="form-control"
                value={
                  form.document_code
                }
                onChange={event =>
                  setForm({
                    ...form,
                    document_code:
                      event.target.value
                  })
                }
                placeholder="SOP-DEP-001"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Version *
              </label>
              <input
                className="form-control"
                value={form.version}
                onChange={event =>
                  setForm({
                    ...form,
                    version:
                      event.target.value
                  })
                }
                placeholder="1.0"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Title *
            </label>
            <input
              className="form-control"
              value={form.title}
              onChange={event =>
                setForm({
                  ...form,
                  title:
                    event.target.value
                })
              }
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Category *
              </label>
              <select
                className="form-control"
                value={form.category}
                onChange={event =>
                  setForm({
                    ...form,
                    category:
                      event.target.value
                  })
                }
              >
                {CATEGORIES.map(
                  category => (
                    <option
                      key={category}
                      value={category}
                    >
                      {category}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Document Type
              </label>

              {uploadOnly ? (
                <div
                  className="form-control"
                  style={{
                    background:'#F2EFE7',
                    color:'#556B2F',
                    fontWeight:600
                  }}
                >
                  PDF Upload
                </div>
              ) : (
                <select
                  className="form-control"
                  value={form.doc_type}
                  onChange={event =>
                    setForm({
                      ...form,
                      doc_type:
                        event.target.value
                    })
                  }
                >
                  {editing && document.doc_type === 'pdf' && (
                    <option value="pdf">Current PDF</option>
                  )}
                  <option value="inline">
                    Bloans Inline SOP
                  </option>
                  <option value="google_doc">
                    Google Document
                  </option>
                </select>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              Description
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={form.description}
              onChange={event =>
                setForm({
                  ...form,
                  description:
                    event.target.value
                })
              }
            />
          </div>

          <AudienceFields
            form={form}
            setForm={setForm}
            roles={roles}
          />

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Effective Date
              </label>
              <input
                type="date"
                className="form-control"
                value={
                  form.effective_date
                }
                onChange={event =>
                  setForm({
                    ...form,
                    effective_date:
                      event.target.value
                  })
                }
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                Review Date
              </label>
              <input
                type="date"
                className="form-control"
                value={
                  form.review_date
                }
                onChange={event =>
                  setForm({
                    ...form,
                    review_date:
                      event.target.value
                  })
                }
              />
            </div>
          </div>

          {form.doc_type ===
            'inline' && (
            <div className="form-group">
              <label className="form-label">
                SOP Content *
              </label>
              <textarea
                className="form-control"
                rows={20}
                value={form.content}
                onChange={event =>
                  setForm({
                    ...form,
                    content:
                      event.target.value
                  })
                }
                style={{
                  fontFamily:
                    'inherit',
                  lineHeight:1.6
                }}
              />
            </div>
          )}

          {form.doc_type ===
            'google_doc' && (
            <div className="form-group">
              <label className="form-label">
                Google Document URL *
              </label>
              <input
                className="form-control"
                value={
                  form.google_doc_url
                }
                onChange={event =>
                  setForm({
                    ...form,
                    google_doc_url:
                      event.target.value
                  })
                }
              />
            </div>
          )}

          {form.doc_type === 'pdf' &&
           !editing && (
            <div
              className="form-group"
              style={{
                padding:18,
                border:'2px dashed #A8893D',
                borderRadius:12,
                background:'#F2EFE7'
              }}
            >
              <label className="form-label">
                Select PDF Document *
              </label>

              <input
                type="file"
                accept="application/pdf,.pdf"
                className="form-control"
                onChange={event =>
                  setFile(
                    event.target
                      .files[0] ||
                    null
                  )
                }
              />

              <div
                className="form-hint"
                style={{marginTop:8}}
              >
                PDF only. Maximum file size:
                20 MB. Uploading creates a
                new SOP draft and does not
                alter any published SOP.
              </div>

              {file && (
                <div
                  style={{
                    marginTop:10,
                    color:'#556B2F',
                    fontSize:13,
                    fontWeight:600
                  }}
                >
                  Selected: {file.name}
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              Publication / Revision Summary *
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={
                form.change_summary
              }
              onChange={event =>
                setForm({
                  ...form,
                  change_summary:
                    event.target.value
                })
              }
              placeholder="Explain what this version introduces or changes."
            />
            <div className="form-hint">
              Required when publishing,
              not when saving a draft.
            </div>
          </div>
        </div>

        <div className="modal-ftr">
          <button
            className="btn btn-outline"
            onClick={onClose}
          >
            Cancel
          </button>

          {!isPublished && (
            <button
              className="btn btn-outline"
              disabled={saving}
              onClick={saveDraft}
            >
              {saving
                ? 'Saving...'
                : 'Save Draft'}
            </button>
          )}

          <button
            className="btn btn-primary"
            disabled={saving}
            onClick={publish}
          >
            {saving
              ? 'Publishing...'
              : isPublished
                ? 'Publish New Version'
                : 'Publish SOP'}
          </button>
        </div>
      </div>
    </div>
  );
}
